import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * Estado del bloqueo del segundo factor por intentos (429 `TOTP_LOCKED`).
 *
 * - `until`: marca de tiempo (ms) en que vence; `null` si banca no dijo cuánto.
 */
export interface TotpLockState {
  readonly until: number | null;
}

/**
 * Ámbito del bloqueo, espejo de `stp_api.totp_guard` en banca:
 *
 * - `principal`: el del USUARIO. Lo cuentan el login con contraseña y todos los
 *   step-ups.
 * - `recuperacion`: el de la recuperación de contraseña. Su `mfa_token` se obtiene
 *   con correo + teléfono, SIN contraseña, así que banca lo cuenta APARTE: si sus
 *   fallos bloquearan al usuario, cualquiera que conociera esos dos datos podría
 *   dejarlo sin login ni transferencias quince minutos.
 *
 * Y la asimetría de banca (`bloquean=(PRINCIPAL, RECUPERACION)` en la recuperación):
 * la recuperación la frenan LOS DOS; el login, solo el principal.
 */
export type TotpLockScope = 'principal' | 'recuperacion';

type Locks = Readonly<Record<TotpLockScope, TotpLockState | null>>;

const EMPTY: Locks = { principal: null, recuperacion: null };

/**
 * El bloqueo del 2FA es del USUARIO, no de una pantalla: banca lo aplica a todos
 * sus step-ups y al login. Por eso vive en UN sitio (`root`) y lo alimenta UN
 * interceptor (`TotpLockInterceptor`) que ve todas las respuestas; la ventana
 * compartida `<app-two-factor-auth>` lo lee y lo pinta.
 *
 * - Con segundos: se libera solo al vencer.
 * - Sin segundos: dura hasta que la ventana que lo mostró se CIERRA
 *   (`releaseUntimed`); no hay forma honesta de saber cuándo vence.
 * - ⚠️ **Se limpia al cerrar sesión** (`clear`, desde `LogoutService`): es un
 *   singleton en memoria y el cierre de sesión navega sin recargar, así que sin
 *   eso el SIGUIENTE usuario de la pestaña heredaba el bloqueo del anterior.
 *
 * No guarda nada en `sessionStorage` ni registra nada: es un estado de pantalla.
 */
@Injectable({ providedIn: 'root' })
export class TotpLockService implements OnDestroy {
  private readonly subject = new BehaviorSubject<Locks>(EMPTY);
  private readonly timers: Partial<Record<TotpLockScope, ReturnType<typeof setTimeout>>> = {};

  /** El bloqueo que frena al login y a los step-ups (ámbito principal). */
  readonly state$: Observable<TotpLockState | null> = this.stateFor$('principal');

  /** Fija el bloqueo de un ámbito. `seconds` nulo ⇒ sin vencimiento conocido. */
  lock(seconds: number | null, now: number = Date.now(), scope: TotpLockScope = 'principal'): void {
    this.clearTimer(scope);
    if (seconds && seconds > 0) {
      this.set(scope, { until: now + seconds * 1000 });
      this.timers[scope] = setTimeout(() => this.release(scope), seconds * 1000);
      return;
    }
    this.set(scope, { until: null });
  }

  /**
   * Lo que frena a una ventana de ese ámbito. La recuperación la frenan los dos
   * (gana el que más dura; uno sin plazo, a los dos); el login, solo el principal.
   */
  stateFor$(scope: TotpLockScope): Observable<TotpLockState | null> {
    return this.subject.pipe(
      map((locks) => effective(locks, scope)),
      distinctUntilChanged((a, b) => a?.until === b?.until && (a === null) === (b === null)),
    );
  }

  /** Cerrar la ventana libera un bloqueo SIN vencimiento de su ámbito; uno con plazo sigue. */
  releaseUntimed(scope: TotpLockScope = 'principal'): void {
    const state = this.subject.value[scope];
    if (state && state.until === null) this.release(scope);
  }

  /** Cierre de sesión: el bloqueo era de ESE usuario, no de la pestaña. */
  clear(): void {
    this.clearTimer('principal');
    this.clearTimer('recuperacion');
    this.subject.next(EMPTY);
  }

  get current(): TotpLockState | null {
    return this.subject.value.principal;
  }

  currentFor(scope: TotpLockScope): TotpLockState | null {
    return effective(this.subject.value, scope);
  }

  ngOnDestroy(): void {
    this.clearTimer('principal');
    this.clearTimer('recuperacion');
  }

  private release(scope: TotpLockScope): void {
    this.clearTimer(scope);
    this.set(scope, null);
  }

  private set(scope: TotpLockScope, state: TotpLockState | null): void {
    this.subject.next({ ...this.subject.value, [scope]: state });
  }

  private clearTimer(scope: TotpLockScope): void {
    const timer = this.timers[scope];
    if (timer) clearTimeout(timer);
    delete this.timers[scope];
  }
}

function effective(locks: Locks, scope: TotpLockScope): TotpLockState | null {
  if (scope === 'principal') return locks.principal;
  const a = locks.principal;
  const b = locks.recuperacion;
  if (!a || !b) return a ?? b;
  if (a.until === null || b.until === null) return { until: null };
  return a.until >= b.until ? a : b;
}
